import { SrcFieldValue } from '../../../../constnats/reportConstants';
import type { Descriptor } from '../../../../models/descriptorModel';
import type { ReportType } from '../../../../schemas/report.schemas';
import type { ReconciliationEvent } from '../../../../types/reconciliation.types';
import { getReportUniqueBusinessKey } from '../../../../utils/common';
import { normalizeValue } from '../../../../utils/normalize';
import ReportUtils from '../../../../utils/reportUtils';
import ReportsRepo from '../../../../utils/repository/repoertsRepo';
import TableUpdatesRepo from '../../../../utils/repository/tableUpdatesRepo';
import EntityFactory from '../components/financialTables/table/cellEntity';
import { RECONCILIATION_OPERATION } from '../constants/peerDiff';
import type { PeerBranchesActions } from '../stores/peersReportsStore';

const BRACKET_PAIRS = [
    ['income', 'loss'],
    ['provision', 'benefit'],
    ['gains', 'losses'],
    ['gain', 'loss'],
    ['earnings', 'loss'],
    ['income', 'expense'],
    ['benefit', 'expense'],
    ['above', 'below'],
    ['increase', 'decrease'],
    ['proceeds', 'payments'],
    ['expense', 'recovery'],
    ['benefit', 'cost'],
    ['profit', 'loss'],
    ['expenses', 'income'],
    ['charges', 'recoveries'],
    ['earnings', 'losses'],
    ['discount', 'premium'],
    ['income', 'losses'],
    ['recognition', 'reversal'],
    ['reversal', 'expense'],
    ['provided by', 'used in'],
    ['provision for', 'reversal of'],
    ['benefit from', 'provision for'],
    ['costs', 'income'],
    ['reimbursements', 'expenses'],
    ['costs', 'recoveries'],
    ['surplus', 'deficit'],
    ['write-offs', 'recoveries'],
    ['provisions', 'release of provisions'],
    ['inflows', 'outflows'],
    ['costs', 'benefit'],
    ['benefit', 'charge'],
    ['charges', 'income'],
    ['gains', 'charges'],
    ['appreciation', 'depreciation'],
    ['costs','credits'],
];

const CONVERSIONS = BRACKET_PAIRS
    .flatMap(([first, second]) => [[first, second], [second, first]])
    .flatMap(([first, second]) => {
        const firstPattern = first.replace(/ /g, '\\s+');
        const secondPattern = second.replace(/ /g, '\\s+');
        return [
            new RegExp(`\\b(${firstPattern})\\s*/?\\s*\\(\\s*(${secondPattern})\\s*\\)`, 'gi'),
            new RegExp(`\\(\\s*(${firstPattern})\\s*\\)\\s*/?\\s*(${secondPattern})\\b`, 'gi'),
        ];
    });

const DASH_SPACING_PATTERN = /\s*(\p{Dash})\s*/gu;

export function convertLabel(label: string): string {
    let converted = label;
    for (const pattern of CONVERSIONS) {
        converted = converted.replace(pattern, '$1/$2');
    }
    return converted.replace(DASH_SPACING_PATTERN, '$1');
}

export function labelConversionResolvesDiff(current: string, peer: string): boolean {
    const converted = convertLabel(current);
    return converted !== current &&
        normalize(converted) === normalize(peer);
}

function normalize(label: string): string {
    return normalizeValue(label.toLowerCase().replace(DASH_SPACING_PATTERN, '$1')) ?? '';
}

export type LabelConversionBatch = {
    report: ReportType & { schemaId: number };
    events: ReconciliationEvent[];
};

type CollectOptions = {
    reports: ReportType[];
    descriptors: Descriptor[];
    schemaId: number;
    schemaName: string;
    getPeerReportDiffDetector: PeerBranchesActions['actions']['getPeerReportDiffDetector'];
};

export function collectPeerLabelConversions({
    reports, descriptors, schemaId, schemaName, getPeerReportDiffDetector,
}: CollectOptions): LabelConversionBatch[] {
    const batches: LabelConversionBatch[] = [];
    for (const report of reports) {
        if (report.src === SrcFieldValue.CALCULATED) {
            continue;
        }
        const peerReportsDiffDetector = getPeerReportDiffDetector(getReportUniqueBusinessKey(report), report.id, schemaName);
        const entities = new EntityFactory(() => {}).create(descriptors, report);
        const events: ReconciliationEvent[] = [];

        for (const entity of entities) {
            const descriptorId = entity.descriptorId();
            const metadata = entity.getMetadata();
            const currentLabel = metadata?.reportedAsLabel ?? '';
            const diffs = peerReportsDiffDetector({
                descriptorId,
                coreValue: entity.getData(),
                fieldSpecificMetadata: metadata,
                reportSrc: report.src,
            });
            const match = diffs
                .flatMap(diff => diff.getPropDiffs())
                .find(diff => diff.prop === 'reportedAsLabel' && diff.applyable &&
                    labelConversionResolvesDiff(currentLabel, diff.peerValue));
            if (!match) {
                continue;
            }

            entity.setMetadata({ ...metadata, reportedAsLabel: match.peerValue });
            events.push({
                schemaId, reportId: report.id, descriptorId,
                property: 'REPORTED_AS_LABEL',
                currentValue: match.currentValue, targetValue: match.peerValue,
                operation: RECONCILIATION_OPERATION.ACCEPT, riskLevel: match.riskLevel,
            });
        }
        if (events.length) {
            // The legacy serializer builds report fields dynamically, so its inferred type is incomplete.
            const updatedReport = ReportUtils.cellEntitiesToReport(entities, report) as unknown as ReportType;
            batches.push({
                report: { ...updatedReport, schemaId },
                events,
            });
        }
    }
    return batches;
}

export type LabelConversionResult = {
    acceptedCount: number;
    failure?: { reportId: number; stage: 'report' | 'events'; savedLabelCount: number };
};

// Reports and reconciliation events use separate endpoints. Stop at the first failure so
// callers can distinguish completed acceptances from saved labels with unrecorded events.
export async function savePeerLabelConversions(
    batches: LabelConversionBatch[],
    updateId: number,
    onEventsRecorded: (events: ReconciliationEvent[]) => void,
): Promise<LabelConversionResult> {
    let acceptedCount = 0;
    for (const { report, events } of batches) {
        try {
            await new Promise((resolve, reject) => {
                ReportsRepo.update(report, resolve, reject);
            });
        } catch {
            return { acceptedCount, failure: { reportId: report.id, stage: 'report', savedLabelCount: 0 } };
        }
        try {
            await new Promise((resolve, reject) => {
                TableUpdatesRepo.addReconciliationEvents(updateId, { updateId, events }, resolve, reject);
            });
        } catch {
            return { acceptedCount, failure: { reportId: report.id, stage: 'events', savedLabelCount: events.length } };
        }
        acceptedCount += events.length;
        onEventsRecorded(events);
    }
    return { acceptedCount };
}
